import {
  Box,
  Typography,
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { STORE_ID } from "../Config/Constants";
import theme from "../theme";
import TicketCard from "./TicketCard";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  format,
  startOfWeek,
  endOfWeek,
  parseISO,
  eachDayOfInterval,
} from "date-fns";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { useSafeAreaBottom, useSafeAreaTop } from "../SafeArea";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "../components/LoadingScreen";

// Icons
import searchIcon from "../assets/icons/search.svg";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ModeEditRoundedIcon from "@mui/icons-material/ModeEditRounded";
import { ReactComponent as DoneIcon } from "../assets/icons/doneIcon.svg";
import { ReactComponent as ReplayCircleFilledIcon } from "../assets/icons/refresh.svg";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import SourceRoundedIcon from "@mui/icons-material/SourceRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import { ReactComponent as ExportExcelIcon } from "../assets/icons/export-as-excel.svg";
import { whatsAppFailureText } from "../services/wpService";
import FollowUpCard from "./Followupcard";
import { useTimer } from "../Context/TimerContext";

const AssignedTickets = ({
  tickets,
  locationUserID,
  setApiToggle,
  assignedToTicket,
  followUpTickets,
  user_id,
  store_id,
  followUpStatus,
  setFollowUpStatus,
}) => {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const topInset = useSafeAreaTop();
  const bottomInset = useSafeAreaBottom();
  const isIOS = Capacitor.getPlatform() === "ios";

  const safeAreaTop = topInset;
  const safeAreaBottom = bottomInset;

  const navigate = useNavigate();

  const timer = useTimer();

  // State variables
  const [searchText, setSearchText] = useState(() => {
    return sessionStorage.getItem("assignedTicketsSearch") || "";
  });

  const [resolvedToggle, setResolvedToggle] = useState(false);
  const [days, setDays] = useState([]);
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("assignedTicketsTab");
    return saved ? JSON.parse(saved) : { tab: "in_progress" };
  });
  const [filterByDate, setFilterByDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [currentView, setCurrentView] = useState(
    localStorage.getItem("currentViewOfCard") || "card",
  );
  const [showFolders, setShowFolders] = useState(() => {
    return sessionStorage.getItem("isShowFolders") || false;
  });

  const [selectedTickets, setSelectedTickets] = useState([]);

  const [pendingDate, setPendingDate] = useState();
  const [followupDate, setFollowupDate] = useState(null);
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [allFolders, setAllFolders] = useState([]);
  const [folderName, setFolderName] = useState("");
  const [refreshFolder, setRefreshFolder] = useState(true);
  const [editFolderOpen, setEditFolderOpen] = useState(false);
  const [editFolderName, setEditFolderName] = useState("");
  const [selectedEditFolder, setSelectedEditFolder] = useState(null);
  const [selectedFolder, setSelecetdFolder] = useState(null);

  const isFirstRender = React.useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    sessionStorage.setItem("assignedTicketsSearch", searchText);
    sessionStorage.setItem("isShowFolders", showFolders);
  }, [searchText, showFolders]);

  // Pending count — exclude tickets with pm_id
  const pendingCount = tickets.filter((ticket) => {
    if (ticket.status !== "in_progress" || ticket.today_flag !== 0) {
      return false;
    }

    if (ticket.pm_id) return false;

    const startDate = new Date(ticket.start_date).toISOString().split("T")[0];

    if (pendingDate) {
      return startDate <= pendingDate;
    }

    const today = new Date();
    const twoDaysLater = new Date();
    twoDaysLater.setDate(today.getDate() + 2);

    return new Date(ticket.start_date) < twoDaysLater;
  }).length;

  // Project count — in_progress && has pm_id
  const projectCount = tickets.filter(
    (ticket) =>
      ticket.status === "in_progress" &&
      ticket.today_flag === 0 &&
      !!ticket.pm_id,
  ).length;

  // Today Count
  const todayCount = tickets.filter(
    (ticket) => ticket.status === "in_progress" && ticket.today_flag === 1,
  ).length;

  // Assigned to count
  const assignedTo = assignedToTicket.filter(
    (ticket) =>
      ticket.status === "in_progress" &&
      ticket.assigned_to !== ticket.assigned_from,
  ).length;

  // Resolved Count – exclude follow‑up tickets
  const resolvedCount = tickets.filter((ticket) => {
    if (ticket.status !== "resolved") return false;
    if (ticket.time_taken < 10) return false;
    if (ticket.is_follow_up == 1) return false; // ← follow‑ups excluded

    if (filterByDate) {
      const updatedDate = new Date(ticket.updated_at)
        .toISOString()
        .split("T")[0];
      return updatedDate === filterByDate;
    }

    return true;
  }).length;

  // Follow Up Count – now dynamic based on followUpTickets (which is filtered by parent)
  const followUpTicketCount = (followUpTickets || []).length;

  const FILTER_LABELS = {
    assigned_tickets: `Assigned To (${assignedTo})`,
    in_progress: `Pending (${pendingCount})`,
    today: `Today (${todayCount})`,
    project: `Project (${projectCount})`,
    follow_up: `Follow Up (${followUpTicketCount})`,
    resolved: `Resolved (${resolvedCount})`,
  };

  // Filter follow-up tickets
  const filteredFollowUp = assignedToTicket.filter(
    (ticket) =>
      (ticket.status === "in_progress" || ticket.status === "resolved") &&
      ticket.assigned_to !== ticket.assigned_from,
  );

  // Fetch all Folders
  useEffect(() => {
    const fetchFolders = async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/folders/user/${user_id}?store_id=${store_id}`;
      try {
        const { data } = await axios.get(url);
        if (data?.success) {
          setAllFolders(data?.folders);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchFolders();
  }, [refreshFolder]);

  // Compute week days based on selected date
  useEffect(() => {
    if (filterByDate) {
      const selectedDate = parseISO(filterByDate);
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekDays = eachDayOfInterval({ start, end });
      setDays(weekDays);
    }
  }, [filterByDate]);

  // Snackbar close handler
  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackBarConfig((prev) => ({ ...prev, open: false }));
  };

  // Tab change handler
  const handleTabChange = (value) => {
    const newFilters = { tab: value };
    setFilters(newFilters);
    localStorage.setItem("assignedTicketsTab", JSON.stringify(newFilters));
    if (value !== "in_progress" && value !== "project") {
      setSelecetdFolder(null);
    }
  };

  // Filter tickets based on tab, date, resolved toggle & search
  const filteredTickets =
    filters.tab === "follow_up"
      ? (followUpTickets || []).filter((ticket) => {
          if (!searchText) return true;
          const query = searchText.toLowerCase();
          return (
            (ticket.ticket_number &&
              ticket.ticket_number.toString().toLowerCase().includes(query)) ||
            (ticket.store_name &&
              ticket.store_name.toLowerCase().includes(query))
          );
        })
      : filters.tab === "assigned_tickets"
      ? filteredFollowUp.filter((ticket) => {
          const baseDate = resolvedToggle
            ? ticket.updated_at
            : ticket.created_at;
          const formattedDate = new Date(baseDate).toISOString().split("T")[0];

          if (followupDate && formattedDate !== followupDate) return false;
          if (resolvedToggle && ticket.status !== "resolved") return false;
          if (!resolvedToggle && ticket.status === "resolved") return false;

          if (!searchText) return true;
          const query = searchText.toLowerCase();
          return (
            (ticket.ticket_number &&
              ticket.ticket_number.toString().toLowerCase().includes(query)) ||
            (ticket.assigned_to_name &&
              ticket.assigned_to_name.toLowerCase().includes(query)) ||
            (ticket.store_name &&
              ticket.store_name.toLowerCase().includes(query))
          );
        })
      : tickets
          .filter((ticket) => {
            console.log(ticket);
            if (filters.tab === "in_progress") {
              if (ticket.status !== "in_progress" || ticket.today_flag !== 0) {
                return false;
              }
              if (ticket.pm_id) return false;

              const startDate = new Date(ticket.start_date)
                .toISOString()
                .split("T")[0];

              if (pendingDate) {
                return startDate <= pendingDate;
              }

              const today = new Date();
              const twoDaysLater = new Date();
              twoDaysLater.setDate(today.getDate() + 2);
              return new Date(ticket.start_date) <= twoDaysLater;
            }

            if (filters.tab === "project") {
              return (
                ticket.status === "in_progress" &&
                ticket.today_flag === 0 &&
                !!ticket.pm_id
              );
            }

            if (filters.tab === "resolved") {
              // ❌ Exclude follow‑up tickets
              if (ticket.is_follow_up == 1) return false;

              const updatedDate = new Date(ticket.updated_at)
                .toISOString()
                .split("T")[0];
              if (filterByDate && updatedDate !== filterByDate) return false;
              if (resolvedToggle && ticket.status !== "resolved") return false;
              return ticket.status === "resolved";
            }

            if (filters.tab === "today") {
              return ticket.today_flag === 1 && ticket.status === "in_progress";
            }

            return false;
          })
          .filter((ticket) => {
            if (!searchText) return true;
            const query = searchText.toLowerCase();
            return (
              (ticket.ticket_number &&
                ticket.ticket_number
                  .toString()
                  .toLowerCase()
                  .includes(query)) ||
              (ticket.store_name &&
                ticket.store_name.toLowerCase().includes(query))
            );
          });

  const applyFolderFilter = (tickets, selectedFolder, currentTab) => {
    if (currentTab === "pending") {
      if (!selectedFolder) {
        return tickets.filter((ticket) => !ticket.folder_id);
      } else {
        return tickets.filter(
          (ticket) => ticket.folder_id === selectedFolder.id,
        );
      }
    } else if (currentTab === "today" || currentTab === "resolved") {
      if (!selectedFolder) {
        return tickets;
      } else {
        return tickets.filter(
          (ticket) => ticket.folder_id === selectedFolder.id,
        );
      }
    } else {
      if (!selectedFolder) {
        return tickets.filter((ticket) => !ticket.folder_id);
      } else {
        return tickets.filter(
          (ticket) => ticket.folder_id === selectedFolder.id,
        );
      }
    }
  };

  const folderFilteredTickets =
    filters.tab === "follow_up"
      ? filteredTickets // follow-up tickets don't use folders
      : applyFolderFilter(filteredTickets, selectedFolder, filters.tab);

  // Total time budget & taken
  const totalTimeBudget = folderFilteredTickets.reduce(
    (sum, ticket) => sum + Number(ticket.time_budget || 0),
    0,
  );
  const totalTimeTaken = folderFilteredTickets.reduce(
    (sum, ticket) => sum + Number(ticket.time_taken || 0),
    0,
  );
  const folderTicketCounts = allFolders.map((folder) => {
    const count = filteredTickets.filter(
      (ticket) => ticket.folder_id === folder.id,
    ).length;
    return { ...folder, ticketCount: count };
  });

  const handleFolderSelect = (folder) => {
    setSelecetdFolder(folder);
  };
  const handleClearFolder = () => {
    setSelecetdFolder(null);
    sessionStorage.removeItem("isShowFolders");
  };

  // API call to mark ticket for today
  const updateToday = async (ticket) => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/updateTodayBulk`;
      const payload = [
        {
          ticket_id: ticket.id,
          today_flag: "true",
          assigned_to: ticket.assigned_to,
        },
      ];
      const response = await axios.post(url, payload);
      if (response.data.statusCode === 200) {
        setSnackBarConfig({
          open: true,
          message: "Ticket has been moved to Today",
          severity: "success",
        });
      }
      setApiToggle((prev) => !prev);
    } catch (error) {
      console.error(error);
    }
  };

  // Tickets with a resolve request in flight. The card's Resolve is a plain
  // Box with no disabled state, so a double tap fired two requests.
  const resolvingIdsRef = useRef(new Set());

  // ✅ API call to mark ticket as resolved + WhatsApp notification
  const updateResolved = async (ticket) => {
    if (resolvingIdsRef.current.has(ticket.id)) return;
    if (!ticket.time_budget) {
      setSnackBarConfig({
        open: true,
        message: "Time Budget and Time Taken are required!",
        severity: "error",
      });
      return;
    }
    resolvingIdsRef.current.add(ticket.id);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/updateticketsbulk`;
      const payload = [
        {
          ticket_id: ticket.id,
          status: "resolved",
          time_taken: ticket.time_budget,
          pm_id: ticket.pm_id || null,
        },
      ];
      const response = await axios.put(url, payload);
      if (response.data.statusCode === 200) {
        // The backend sends the store group its "Ticket Resolved" message and
        // reports here only when that failed.
        const whatsAppWarning = whatsAppFailureText(
          response.data.whatsapp?.[0],
        );
        setSnackBarConfig({
          open: true,
          message: whatsAppWarning || "Tickets updated successfully",
          severity: whatsAppWarning ? "warning" : "success",
        });

        // The resolve stopped the work timer server-side. Ask for the new
        // truth so the running clock in the header/cards goes away now,
        // instead of ticking on until the next minute's re-check.
        timer.resync?.();
      }
      setApiToggle((prev) => !prev);
    } catch (error) {
      console.error(error);
    } finally {
      resolvingIdsRef.current.delete(ticket.id);
    }
  };

  // Handle day selection
  const handleDayClick = (day) => {
    const formatted = format(day, "yyyy-MM-dd");
    setFilterByDate(formatted);
  };

  // Refresh handler
  const handleRefresh = () => setApiToggle((prev) => !prev);

  // Toggle resolved button state
  const handleResolveClick = () => setResolvedToggle(!resolvedToggle);

  // Drag & drop feature
  const handleTicketSelect = (e, id) => {
    if (e.ctrlKey || e.metaKey) {
      setSelectedTickets((prev) =>
        prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
      );
    } else {
      setSelectedTickets([id]);
    }
  };
  const handleDragStart = (e, id) => {
    const draggingTickets =
      selectedTickets.length > 0 && selectedTickets.includes(id)
        ? selectedTickets
        : [id];

    e.dataTransfer.setData("tickets", JSON.stringify(draggingTickets));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDrop = async (e, folderName) => {
    e.preventDefault();
    const tickets = JSON.parse(e.dataTransfer.getData("tickets"));
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/tickets/${tickets[0]}/move`;
    const payload = { folder_id: folderName?.id, user_id: user_id };
    try {
      const response = await axios.put(url, payload);
      if (response?.data?.success) {
        setSnackBarConfig({
          open: true,
          message: "Ticket moved successfully",
          severity: "success",
        });
        setApiToggle((prev) => !prev);
        return;
      }
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: "Something went wrong! Try again",
        severity: "error",
      });
    }
    setSelectedTickets([]);
  };

  // Export tickets data as excel
  const handleExportExcel = (ticketsData) => {
    const worksheet = XLSX.utils.json_to_sheet(ticketsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ticket Data");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(
      data,
      `${ticketsData[0].assigned_to_name} ${
        filters.tab === "assigned_tickets"
          ? "Follow-up"
          : filters.tab === "project"
          ? "Project"
          : "Pending"
      } Tickets.xlsx`,
    );
  };

  const handleCreateFolder = async () => {
    if (!folderName?.trim()) {
      setSnackBarConfig({
        open: true,
        message: "Please Enter Folder name",
        severity: "error",
      });
      return;
    }
    const payload = {
      folder_name: folderName,
      store_id: store_id,
      user_id: user_id,
    };
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/folders/create`;

    try {
      const response = await axios.post(url, payload);

      if (response?.data?.success) {
        setSnackBarConfig({
          open: true,
          message: "Folder created successfully!",
          severity: "success",
        });
        setFolderName("");
      }
      setRefreshFolder((prev) => !prev);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error,
        severity: "error",
      });
      setFolderName("");
    }
  };
  const handleEditFolderClick = (folder) => {
    setSelectedEditFolder(folder);
    setEditFolderName(folder.folder_name);
    setEditFolderOpen(true);
  };
  const handleUpdateFolder = async () => {
    if (!editFolderName.trim()) {
      setSnackBarConfig({
        open: true,
        message: "Please enter folder name",
        severity: "error",
      });
      return;
    }

    if (!selectedEditFolder?.id) {
      setSnackBarConfig({
        open: true,
        message: "Invalid folder selected",
        severity: "error",
      });
      return;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/folders/${selectedEditFolder.id}/rename`;

    const payload = {
      folder_name: editFolderName.trim(),
      user_id: user_id,
    };

    try {
      const response = await axios.put(url, payload);

      if (response?.data?.success) {
        setSnackBarConfig({
          open: true,
          message: "Folder name updated successfully",
          severity: "success",
        });

        setRefreshFolder((prev) => !prev);
        setEditFolderOpen(false);
        setSelectedEditFolder(null);
        setEditFolderName("");
      } else {
        setSnackBarConfig({
          open: true,
          message: response?.data?.message || "Update failed",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackBarConfig({
        open: true,
        message: "Something went wrong! Try again...",
        severity: "error",
      });
    }
  };
  const handleDeleteFolder = async (folder) => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/folders/${folder?.id}/${user_id}`;

      const response = await axios.delete(url);
      if (response?.data?.success) {
        setSnackBarConfig({
          open: true,
          message: "Folder Deleted Successfully",
          severity: "success",
        });
        setRefreshFolder((prev) => !prev);
        setApiToggle((prev) => !prev);

        return;
      }
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: "Something went wrong! Try again...",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          mt: !isMobile ? 1 : 0,
          mb: 10,
          mx: "auto",
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        {/* Filter Tabs */}
        <Box
          sx={{
            width: isMobile ? "100vw" : "100%",
            marginX: -2,
            boxShadow: !isMobile ? "0 2px 5px #bdbdbd" : "none",
            bgcolor: "#fff",
          }}
        >
          {isMobile ? (
            // Mobile: Dropdown
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100vw",
                px: 2,
                py: 1,
                position:
                  store_id === STORE_ID && isMobile ? "fixed" : "static",
                background: isMobile ? theme.lightTheme.header.home : "#fff",
                height: "50px",
                alignItems: "center",
                zIndex: 1000,
              }}
            >
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={filters.tab}
                  onChange={(e) => handleTabChange(e.target.value)}
                  displayEmpty
                  sx={{
                    bgcolor: "white",
                    borderRadius: "8px",
                    fontSize: 14,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.23)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.87)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(68, 40, 160, 1)",
                    },
                  }}
                >
                  {Object.entries(FILTER_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key} sx={{ fontSize: 14 }}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            // Desktop: Tab row – full width, no truncation
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                px: 2,
                background: "#fff",
                height: "36px",
                alignItems: "center",
                gap: 1,
              }}
            >
              {Object.entries(FILTER_LABELS).map(([key, label]) => {
                const isActive = filters.tab === key;
                return (
                  <Typography
                    key={key}
                    onClick={() => handleTabChange(key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      flex: 1,
                      background: isActive
                        ? "linear-gradient(90deg,rgba(0, 128, 128, 1) 0%, rgba(68, 40, 160, 1) 100%)"
                        : "#fff",
                      borderRadius: 0,
                      fontSize: 13,
                      cursor: "pointer",
                      color: isActive ? "#fff" : "black",
                      fontWeight: isActive ? 600 : 400,
                      transition: "all 0.2s ease",
                      textAlign: "center",
                    }}
                  >
                    {label}
                  </Typography>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Filters & Search */}
        <Box
          sx={{ width: "100%", mt: store_id === STORE_ID && isMobile ? 8 : 1 }}
        >
          {/* Search + Add Tickets — shown for assigned_tickets, in_progress, project */}
          {(filters.tab === "assigned_tickets" ||
            filters.tab === "in_progress" ||
            filters.tab === "project") && (
            <Box
              sx={{
                mb: 1,
                width: "100%",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
              }}
            >
              {/* Search Field */}
              <TextField
                placeholder="Search Tickets"
                sx={{
                  width: isMobile
                    ? "100%"
                    : filters.tab === "in_progress"
                    ? "60%"
                    : "80%",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    fontSize: "14px",
                    borderRadius: isMobile ? "50px" : "8px",
                    backgroundColor: "#fff",
                    "&.Mui-focused fieldset": { borderColor: "#ccc" },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 15px",
                    height: isMobile ? "40px" : "36px",
                    lineHeight: isMobile ? "40px" : "36px",
                    boxSizing: "border-box",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: theme.textCol.subHeading,
                    opacity: 1,
                    fontSize: "12px",
                    fontStyle: "italic",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ marginRight: 0 }}>
                      <img
                        src={searchIcon}
                        alt="search"
                        style={{ width: 24, height: 24 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchText && (
                    <InputAdornment position="end">
                      <CloseIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSearchText("");
                          sessionStorage.removeItem("assignedTicketsSearch");
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              {/* Date Picker + Refresh — only for in_progress (Pending) */}
              {filters.tab === "in_progress" && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    width: isMobile ? "100%" : "20%",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={pendingDate}
                    onChange={(e) => setPendingDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      "& label.Mui-focused": { color: "inherit" },
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        "&.Mui-focused fieldset": { borderColor: "inherit" },
                      },
                      "& input": { fontSize: 14, height: "18px" },
                    }}
                  />

                  {/* Refresh button (mobile only) */}
                  {isMobile && (
                    <Box
                      sx={{
                        border: "1.5px solid #C1C1C1",
                        borderRadius: 2,
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: "#fff",
                      }}
                    >
                      <ReplayCircleFilledIcon
                        onClick={handleRefresh}
                        style={{
                          width: 20,
                          height: 20,
                          color: "rgba(68, 40, 160, 0.9)",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Add Tickets (desktop only) */}
              {!isMobile && (
                <Box
                  sx={{
                    border: "1.5px solid #C1C1C1",
                    borderRadius: 2,
                    width: "20%",
                    height: "40px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#fff",
                  }}
                >
                  <Typography
                    onClick={() => navigate("/all-service-tickets")}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      borderRadius: 1,
                      color: "#fff",
                      background:
                        "linear-gradient(90deg,rgba(0, 128, 128, 1) 0%, rgba(68, 40, 160, 1) 100%)",
                    }}
                  >
                    Add Tickets +
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Follow-up: Date picker + Resolved button */}
          {filters.tab === "assigned_tickets" && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                mb: 1,
                gap: 1,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  bgcolor: "#FFF",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={followupDate}
                  onChange={(e) => setFollowupDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": { borderColor: "inherit" },
                    },
                    "& input": { padding: "10px", fontSize: 14 },
                  }}
                />
              </Box>

              <Box
                sx={{
                  cursor: "pointer",
                  bgcolor: "#F5FFF4",
                  borderRadius: 2,
                  textTransform: "none",
                  color: "#0E830A",
                  height: "40px",
                  width: "112px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  border: "1px solid #0E830A",
                }}
                onClick={handleResolveClick}
              >
                <Typography sx={{ fontSize: 14 }}>Resolved</Typography>
                {resolvedToggle && (
                  <DoneIcon style={{ width: "24px", height: "24px" }} />
                )}
              </Box>
            </Box>
          )}

          {/* Resolved tab: Date + Weekday selector */}
          {filters.tab === "resolved" && (
            <Grid
              container
              spacing={isMobile ? 1 : 2}
              sx={{ width: "100%", alignItems: "stretch" }}
              columns={isMobile ? 1 : 2}
            >
              <Grid item xs={1} sx={{ display: "flex" }}>
                <Box
                  sx={{
                    width: "100%",
                    bgcolor: "#FFF",
                    p: 1,
                    borderRadius: 2,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    type="date"
                    label="Select Date"
                    size="small"
                    fullWidth
                    value={filterByDate}
                    onChange={(e) => setFilterByDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& label.Mui-focused": { color: "inherit" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "inherit" },
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={1} sx={{ display: "flex" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{
                    width: "100%",
                    bgcolor: "#fff",
                    p: 1,
                    borderRadius: 2,
                    height: "100%",
                  }}
                >
                  {days.map((day, idx) => {
                    const dayStr = format(day, "yyyy-MM-dd");
                    const selected = filterByDate === dayStr;
                    return (
                      <Box
                        key={idx}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        onClick={() => handleDayClick(day)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Typography sx={{ fontSize: 14 }}>
                          {format(day, "EEE")}
                        </Typography>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: 14,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: selected
                              ? "#00ADAD"
                              : theme.lightTheme.resolved.dayPicker.bgCol,
                            color: selected ? "white" : "black",
                            fontWeight: selected ? "bold" : "normal",
                          }}
                        >
                          {format(day, "dd")}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Total time display */}
          {(filters.tab === "resolved" ||
            filters.tab === "today" ||
            filters.tab === "in_progress" ||
            filters.tab === "project") &&
            folderFilteredTickets.length > 0 && (
              <Box
                sx={{
                  mb: 1,
                  height:
                    filters.tab === "today" ||
                    filters.tab === "in_progress" ||
                    filters.tab === "project"
                      ? "36px"
                      : "48px",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  background:
                    "linear-gradient(90deg,rgba(0, 173, 173, 0.6) 0%, rgba(68, 40, 160, 0.6) 100%)",
                }}
              >
                <Box
                  sx={{
                    display:
                      filters.tab === "today" ||
                      filters.tab === "in_progress" ||
                      filters.tab === "project"
                        ? "flex"
                        : "block",
                    justifyContent:
                      filters.tab === "today" ||
                      filters.tab === "in_progress" ||
                      filters.tab === "project"
                        ? "space-between"
                        : "none",
                    px: 2,
                  }}
                  textAlign="center"
                  flex={1}
                >
                  <Typography variant="caption" color="#fff" fontWeight={300}>
                    Total Time Budget
                  </Typography>
                  <Typography fontSize={14} fontWeight={500} color="#fff">
                    {totalTimeBudget} Mins
                  </Typography>
                </Box>
                {filters.tab !== "today" &&
                  filters.tab !== "in_progress" &&
                  filters.tab !== "project" && (
                    <>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ borderColor: "#90CAF9" }}
                      />
                      <Box textAlign="center" flex={1}>
                        <Typography
                          variant="caption"
                          color="#fff"
                          fontWeight={300}
                        >
                          Total Time Taken
                        </Typography>
                        <Typography fontSize={14} fontWeight={500} color="#fff">
                          {totalTimeTaken} Mins
                        </Typography>
                      </Box>
                    </>
                  )}
              </Box>
            )}
        </Box>

        {/* Row: Active/Resolved buttons (left) — View toggle, Export, Folder icons (right) */}
        <Box
          sx={{
            mb: 0,
            mx: "auto",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          {/* LEFT SIDE: folder name (if not follow-up) or Active/Resolved buttons */}
          {filters.tab === "follow_up" ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setFollowUpStatus("follow_up")}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  bgcolor:
                    followUpStatus === "follow_up" ? "#00ADAD" : "#E0E0E0",
                  color: followUpStatus === "follow_up" ? "#fff" : "#000",
                  fontWeight: 600,
                  fontSize: isMobile ? 11 : 13,
                  px: 2,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor:
                      followUpStatus === "follow_up" ? "#00ADAD" : "#D0D0D0",
                  },
                }}
              >
                Active
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setFollowUpStatus("resolved")}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  bgcolor:
                    followUpStatus === "resolved" ? "#00ADAD" : "#E0E0E0",
                  color: followUpStatus === "resolved" ? "#fff" : "#000",
                  fontWeight: 600,
                  fontSize: isMobile ? 11 : 13,
                  px: 2,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor:
                      followUpStatus === "resolved" ? "#00ADAD" : "#D0D0D0",
                  },
                }}
              >
                Resolved
              </Button>
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: isMobile ? 14 : 18,
                fontWeight: "bold",
                bgcolor: "#1ab0bb1a",
                paddingX: 2,
                borderRadius: 1,
                color: "rgba(0, 83, 72, 0.81)",
              }}
            >
              {selectedFolder?.folder_name}
            </Typography>
          )}

          {/* RIGHT SIDE: view toggle, export, folder icons */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "center",
                  border: "0.5px solid #C4C4C4",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1.5,
                    py: 0.5,
                    cursor: "pointer",
                    bgcolor: currentView === "card" ? "#e7efff" : "transparent",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      bgcolor: currentView === "card" ? "#d9e4ff" : "#f5f5f5",
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentView("card");
                  }}
                >
                  <GridViewRoundedIcon
                    sx={{
                      fontSize: 28,
                      fill: currentView === "card" ? "#1c3891" : "#9e9e9e",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newView = "card";
                      setCurrentView(newView);
                      localStorage.setItem("currentViewOfCard", newView);
                    }}
                  />
                </Box>

                <Box sx={{ width: "1px", bgcolor: "#C4C4C4" }} />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1.5,
                    py: 0.5,
                    cursor: "pointer",
                    bgcolor: currentView === "list" ? "#e7efff" : "transparent",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      bgcolor: currentView === "list" ? "#d9e4ff" : "#f5f5f5",
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentView("list");
                  }}
                >
                  <ViewListRoundedIcon
                    sx={{
                      fontSize: 28,
                      fill: currentView === "list" ? "#1c3891" : "#9e9e9e",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newView = "list";
                      setCurrentView(newView);
                      localStorage.setItem("currentViewOfCard", newView);
                    }}
                  />
                </Box>
              </Box>
            )}

            {(filters.tab === "in_progress" ||
              filters.tab === "assigned_tickets" ||
              filters.tab === "project") &&
              folderFilteredTickets.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => handleExportExcel(folderFilteredTickets)}
                >
                  <ExportExcelIcon
                    style={{ height: 28, width: 28, fill: "#1c3891" }}
                  />
                </Box>
              )}

            {(filters.tab === "in_progress" || filters.tab === "project") && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <SourceRoundedIcon
                  sx={{ fontSize: 28, fill: "#1c3891" }}
                  onClick={() => setShowFolders((prev) => !prev)}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Tickets & Folders Panel */}
        <Box
          sx={{
            mb: 0,
            mx: "auto",
            width: "100%",
            display: "flex",
            gap: 1,
            position: "relative",
          }}
        >
          {/* Tickets Panel */}
          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns:
                currentView === "list"
                  ? "1fr"
                  : isMobile
                  ? "1fr"
                  : "repeat(3, 1fr)",
              gap: isMobile ? "8px" : "0 10px",
              transition: "all 0.4s ease",
              marginRight: showFolders ? (isMobile ? "65vw" : "20vw") : 0,
            }}
          >
            {folderFilteredTickets.length === 0 ? (
              <Typography
                sx={{
                  color: theme.textCol.subHeading,
                  mt: 3,
                  textAlign: "center",
                  gridColumn: "1 / -1",
                }}
              >
                No{" "}
                {filters.tab === "in_progress"
                  ? "Pending "
                  : filters.tab === "project"
                  ? "Project "
                  : FILTER_LABELS[filters.tab]}
                tickets found
              </Typography>
            ) : (
              folderFilteredTickets.map((ticket, index) => {
                const isSelected = selectedTickets.includes(ticket.id);

                if (filters.tab === "follow_up") {
                  return (
                    <Box
                      key={`${ticket.id}-${index}`}
                      sx={{
                        borderRadius: 1,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        py: !isMobile ? 0.6 : 0,
                        px: !isMobile ? 0.6 : 0,
                      }}
                    >
                      <FollowUpCard
                        ticket={ticket}
                        onFollowUpSent={() => setApiToggle((prev) => !prev)}
                        setSnackBarConfig={setSnackBarConfig}
                      />
                    </Box>
                  );
                }

                return (
                  <Box
                    key={`${ticket.id}-${index}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, ticket.id)}
                    onClick={(e) => handleTicketSelect(e, ticket.id)}
                    sx={{
                      borderRadius: 1,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      py: !isMobile ? 0.6 : 0,
                      px: !isMobile ? 0.6 : 0,
                      border: isSelected
                        ? "2px solid #1c3891"
                        : "1px solid transparent",
                      backgroundColor: isSelected ? "#e7efff" : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <TicketCard
                      currentView={currentView}
                      filteredTickets={folderFilteredTickets}
                      index={index}
                      staffID={locationUserID}
                      ticket={ticket}
                      tabStatus={filters.tab}
                      markAsResolved={updateResolved}
                      markForToday={updateToday}
                    />
                  </Box>
                );
              })
            )}
          </Box>

          {/* Sliding Folder Panel */}
          {(filters.tab === "in_progress" || filters.tab === "project") && (
            <Box
              sx={{
                position: "fixed",
                right: 0,
                top: !isMobile ? 70 : "unset",
                bottom: 0,
                height: isMobile ? "calc(100vh - 105px)" : "calc(100vh - 80px)",
                zIndex: 2000,
                width: showFolders ? (isMobile ? "56vw" : "20vw") : 0,
                bgcolor: "#f9fafc",
                boxShadow: showFolders
                  ? "-4px 0 12px rgba(0,0,0,0.15)"
                  : "none",
                transition: "width 0.4s ease, box-shadow 0.4s ease",
                display: "flex",
                flexDirection: "column",
                borderTopLeftRadius: isMobile ? 1.5 : 0,
                borderBottomLeftRadius: isMobile ? 1.5 : 0,
                overflow: "hidden",
                borderTop: isMobile ? "1px solid #ccc" : "none",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  pt: isIOS ? "var(--safe-area-top)" : `${safeAreaTop}px`,
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "#f1f3f6",
                  borderTopLeftRadius: isMobile ? "16px" : 0,
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Folders
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowFolders(false)}
                  sx={{
                    color: "#000",
                    fontWeight: 300,
                    height: 24,
                    fontSize: 36,
                  }}
                >
                  -
                </IconButton>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 2,
                  py: 1,
                  pb: isIOS ? "var(--safe-area-bottom)" : `${safeAreaBottom}px`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <TextField
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="Folder Name"
                    size="small"
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": { height: 36 },
                      "& input": { padding: "8px 12px", fontSize: 12 },
                    }}
                  />
                  <Box
                    onClick={handleCreateFolder}
                    sx={{
                      bgcolor: "rgba(0, 88, 146, 1)",
                      height: 36,
                      px: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      borderRadius: 2,
                      cursor: "pointer",
                    }}
                  >
                    <AddIcon sx={{ fontSize: 22 }} />
                  </Box>
                </Box>

                <Box
                  onClick={handleClearFolder}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    px: 1,
                    py: 0.3,
                    position: "relative",
                    flexShrink: 0,
                    cursor: "pointer",
                    borderRadius: 2,
                    bgcolor: "#faedddc4",
                    border: "1px solid #ffca89ff",
                    boxShadow: "-2px 0 8px rgba(0,0,0,0.15)",
                    transition: "all 0.2s ease",
                    "&:hover": { backgroundColor: "#edf3ff" },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <FolderRoundedIcon
                        sx={{ fontSize: isMobile ? 20 : 44, fill: "#f19625ff" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: isMobile ? 10 : 14,
                        fontWeight: 500,
                        color: "#000000c2",
                      }}
                    >
                      Unfiled
                    </Typography>
                  </Box>
                </Box>

                {folderTicketCounts.map((folder, index) => {
                  const isSelected = selectedFolder?.id === folder.id;
                  return (
                    <Box
                      key={folder.id || index}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, folder)}
                      onClick={() => handleFolderSelect(folder)}
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                        px: 1,
                        py: 0.3,
                        position: "relative",
                        flexShrink: 0,
                        cursor: "pointer",
                        borderRadius: 2,
                        bgcolor: isSelected ? "#dbe6ff" : "#F9F9F9",
                        border: isSelected
                          ? "2px solid #1c3891"
                          : "2px solid transparent",
                        boxShadow: isSelected
                          ? "0 0 10px rgba(28,56,145,0.4)"
                          : "-2px 0 8px rgba(0,0,0,0.15)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: isSelected ? "#dbe6ff" : "#edf3ff",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{ position: "relative", display: "inline-flex" }}
                        >
                          <FolderRoundedIcon
                            sx={{
                              fontSize: isMobile ? 20 : 44,
                              fill: isSelected
                                ? "rgba(28, 56, 145, 1)"
                                : "rgba(28, 56, 145, 0.8)",
                            }}
                          />
                          {folder.ticketCount > 0 && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: isMobile ? -2 : 2,
                                left: isMobile ? -2 : 0,
                                width: 12,
                                height: 12,
                                bgcolor: "#e53935",
                                borderRadius: "50%",
                                color: "#fff",
                                fontSize: 8,
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 0 4px rgba(0,0,0,0.3)",
                              }}
                            >
                              {folder.ticketCount}
                            </Box>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: isMobile ? 10 : 14,
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? "#1c3891" : "#000",
                          }}
                        >
                          {folder.folder_name}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", gap: isMobile ? 0.5 : 1 }}>
                        <ModeEditRoundedIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFolderClick(folder);
                          }}
                          sx={{
                            fontSize: isMobile ? 18 : 24,
                            color: "rgba(28, 145, 120, 1)",
                            cursor: "pointer",
                          }}
                        />
                        <DeleteRoundedIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder);
                          }}
                          sx={{
                            fontSize: isMobile ? 18 : 24,
                            color: "rgba(28, 56, 145, 1)",
                            cursor: "pointer",
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={snackBarConfig.open}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{
            "&.MuiSnackbar-root": { bottom: "56px !important" },
            zIndex: 90000,
          }}
        >
          <Alert
            onClose={handleClose}
            severity={snackBarConfig.severity}
            variant="filled"
          >
            {snackBarConfig.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={editFolderOpen}
          onClose={() => setEditFolderOpen(false)}
          fullWidth
          maxWidth="xs"
          disablePortal={false}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 2000 }}
          BackdropProps={{
            sx: {
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0,0,0,0.25)",
            },
          }}
        >
          <DialogTitle>Edit Folder</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Folder Name"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditFolderOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpdateFolder}
              disabled={!editFolderName.trim()}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default AssignedTickets;
